// TanStack Query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
// CD Feature Pages
import CreateCDWizard from './features/closing-disclosure/components/wizard/CreateCDWizard';
import CDDraftsListPage from './features/closing-disclosure/pages/CDDraftsListPage';
import CDPreviewPage from './features/closing-disclosure/pages/CDPreviewPage';
import CreateCDPage from './features/closing-disclosure/pages/CreateCDPage';
import { DocumentValidationPage } from './features/closing-disclosure/pages/DocumentValidationPage';
import TransactionDocumentsPage from './features/closing-disclosure/pages/TransactionDocumentsPage';
import TransactionPreviewPage from './features/closing-disclosure/pages/TransactionPreviewPage';
// Contacts Feature Pages
import {
  CompaniesListPage,
  CompanyDetailPage,
  CreateCompanyPage,
  EditCompanyPage,
} from './features/contacts';
// Orders Feature Pages
import { CreateOrderWizard, OrderDetailPage, OrdersListPage } from './features/orders';
// Auth pages
import AcceptInvite from './pages/auth/AcceptInvite';
import ForgotPassword from './pages/auth/ForgotPassword';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ResetPassword from './pages/auth/ResetPassword';
import VerifyEmail from './pages/auth/VerifyEmail';
// Dashboard pages
import Dashboard from './pages/dashboard/Dashboard';
import Members from './pages/dashboard/Members';
import Organization from './pages/dashboard/Organization';
import Overview from './pages/dashboard/Overview';
import Sessions from './pages/dashboard/Sessions';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function App() {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <Toaster />
            <Routes>
              {/* Public auth routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/verify-email" element={<VerifyEmail />} />
              <Route path="/accept-invite" element={<AcceptInvite />} />

              {/* Protected dashboard routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Overview />} />
                <Route
                  path="members"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Members />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="sessions"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Sessions />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="organization"
                  element={
                    <ProtectedRoute requireAdmin>
                      <Organization />
                    </ProtectedRoute>
                  }
                />
                {/* CD Drafts List */}
                <Route path="drafts" element={<CDDraftsListPage />} />
                {/* CD Creation Route */}
                <Route path="create-cd" element={<CreateCDPage />} />
                {/* CD Edit Route */}
                <Route path="cd/:id/edit" element={<CreateCDWizard />} />
              </Route>

              {/* CD Preview Route (Development - Sample Data) */}
              <Route
                path="/cd-preview"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<CDPreviewPage />} />
              </Route>

              {/* Transaction Preview Route (Real Data) */}
              <Route
                path="/cd/:id/preview"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<TransactionPreviewPage />} />
              </Route>

              {/* Transaction Documents Route */}
              <Route
                path="/cd/:id/documents"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<TransactionDocumentsPage />} />
              </Route>

              {/* Document Validation Route */}
              <Route
                path="/cd/:transactionId/documents/:documentId/validate"
                element={
                  <ProtectedRoute>
                    <DocumentValidationPage />
                  </ProtectedRoute>
                }
              />

              {/* Orders Routes */}
              <Route
                path="/orders"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<OrdersListPage />} />
                <Route path="create" element={<CreateOrderWizard />} />
                <Route path=":id" element={<OrderDetailPage />} />
                <Route path=":id/edit" element={<CreateOrderWizard />} />
              </Route>

              {/* Contacts Routes */}
              <Route
                path="/contacts"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              >
                <Route index element={<CompaniesListPage />} />
                <Route path="create" element={<CreateCompanyPage />} />
                <Route path=":id" element={<CompanyDetailPage />} />
                <Route path=":id/edit" element={<EditCompanyPage />} />
              </Route>

              {/* Redirect root to dashboard */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />

              {/* 404 fallback */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  );
}

export default App;
